import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Users, Phone, FileText, Download as DownloadIcon, LogOut, Search, Filter,
  ChevronDown, ChevronUp, Check, X, Loader2, BarChart3, Clock, UserCheck, Archive,
  MessageSquare, Save, RefreshCw, PenTool, TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";
import AdminBlog from "@/pages/AdminBlog";
import AdminAnalytics from "@/pages/AdminAnalytics";
import { API } from "@/lib/api";

const ESTADOS = [
  { value: "nuevo", label: "Nuevo", color: "bg-blue-500", icon: Clock },
  { value: "pendiente", label: "Pendiente", color: "bg-yellow-500", icon: Clock },
  { value: "contactado", label: "Contactado", color: "bg-green-500", icon: UserCheck },
  { value: "en_proceso", label: "En proceso", color: "bg-purple-500", icon: RefreshCw },
  { value: "cerrado", label: "Cerrado", color: "bg-gray-500", icon: Archive },
];

const ORIGEN_LABELS = {
  contacto_rapido: "Contacto rápido",
  diagnostico_completo: "Diagnóstico",
  lead_magnet: "Lead checklist",
};

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-[#1A1F2E] border border-gray-700/50 rounded-xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <span className="text-gray-400 text-sm">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
    </div>
  );
}

function ContactRow({ contact, onStatusChange, onNotesUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [notas, setNotas] = useState(contact.notas_internas || "");
  const [saving, setSaving] = useState(false);

  const estado = ESTADOS.find(e => e.value === contact.estado) || ESTADOS[0];

  const handleSaveNotas = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/crm/${contact.id}/notas`, { notas }, { withCredentials: true });
      onNotesUpdate(contact.id, notas);
      toast.success("Notas guardadas");
    } catch (err) {
      toast.error("Error al guardar notas");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.patch(`${API}/admin/crm/${contact.id}/estado`, { estado: newStatus }, { withCredentials: true });
      onStatusChange(contact.id, newStatus);
      toast.success("Estado actualizado");
    } catch (err) {
      toast.error("Error al actualizar estado");
    }
  };

  const nombre = contact.nombre || contact.contacto || "—";
  const clinica = contact.clinica || contact.tipo_clinica || "—";
  const fecha = contact.timestamp ? new Date(contact.timestamp).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="border border-gray-700/50 rounded-lg bg-[#1A1F2E] overflow-hidden" data-testid={`contact-row-${contact.id}`}>
      <div className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-800/30 transition-colors" onClick={() => setExpanded(!expanded)}>
        <div className={`w-2.5 h-2.5 rounded-full ${estado.color} flex-shrink-0`}></div>
        <div className="flex-1 min-w-0 grid grid-cols-5 gap-4 items-center">
          <span className="text-white font-medium truncate">{nombre}</span>
          <span className="text-gray-400 text-sm truncate">{contact.email}</span>
          <span className="text-gray-500 text-sm truncate">{clinica}</span>
          <span className="text-xs px-2 py-1 rounded-full bg-gray-800 text-gray-300 w-fit">{ORIGEN_LABELS[contact.formulario_origen] || contact.formulario_origen}</span>
          <span className="text-gray-500 text-sm">{fecha}</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
      </div>

      {expanded && (
        <div className="border-t border-gray-700/50 p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><span className="text-gray-500">Email</span><p className="text-white">{contact.email}</p></div>
            <div><span className="text-gray-500">Teléfono</span><p className="text-white">{contact.telefono || "—"}</p></div>
            <div><span className="text-gray-500">Origen</span><p className="text-white">{ORIGEN_LABELS[contact.formulario_origen]}</p></div>
            <div><span className="text-gray-500">Fecha</span><p className="text-white">{contact.timestamp ? new Date(contact.timestamp).toLocaleString("es-ES") : "—"}</p></div>
            {contact.usa_ia && <div><span className="text-gray-500">Usa IA</span><p className="text-white">{contact.usa_ia}</p></div>}
            {contact.urgencia && <div><span className="text-gray-500">Urgencia</span><p className="text-white">{contact.urgencia}</p></div>}
            {contact.herramientas && <div className="col-span-2"><span className="text-gray-500">Herramientas</span><p className="text-white">{contact.herramientas}</p></div>}
            {contact.areas_ia?.length > 0 && <div className="col-span-2"><span className="text-gray-500">Áreas IA</span><p className="text-white">{contact.areas_ia.join(", ")}</p></div>}
            {contact.observaciones && <div className="col-span-4"><span className="text-gray-500">Observaciones</span><p className="text-white">{contact.observaciones}</p></div>}
          </div>

          <div className="flex items-center gap-4 pt-2 border-t border-gray-700/30">
            <span className="text-gray-400 text-sm">Estado:</span>
            <Select value={contact.estado} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-48 bg-gray-800 border-gray-700 text-white" data-testid={`status-select-${contact.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ESTADOS.map(e => (
                  <SelectItem key={e.value} value={e.value}>
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${e.color}`}></span>
                      {e.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="pt-2 border-t border-gray-700/30">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="w-4 h-4 text-gray-400" />
              <span className="text-gray-400 text-sm">Notas internas</span>
            </div>
            <Textarea value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Añadir notas internas..." className="bg-gray-800 border-gray-700 text-white min-h-[60px] mb-2" data-testid={`notas-input-${contact.id}`} />
            <Button onClick={handleSaveNotas} disabled={saving} size="sm" className="btn-primary text-xs" data-testid={`save-notas-${contact.id}`}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Guardar notas
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterOrigen, setFilterOrigen] = useState("todos");
  const [filterEstado, setFilterEstado] = useState("todos");
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("crm");

  // Check auth
  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => setUser(res.data))
      .catch(() => navigate("/admin/login"))
      .finally(() => setLoading(false));
  }, [navigate]);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const [contactsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/crm`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
      ]);
      setContacts(contactsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    navigate("/admin/login");
  };

  const handleExportCSV = async () => {
    try {
      const res = await axios.get(`${API}/admin/crm/export/csv`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'crm_contactos.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Error al exportar CSV");
    }
  };

  const handleStatusChange = (id, newStatus) => {
    setContacts(prev => prev.map(c => c.id === id ? { ...c, estado: newStatus } : c));
  };

  const handleNotesUpdate = (id, notas) => {
    setContacts(prev => prev.map(c => c.id === id ? { ...c, notas_internas: notas } : c));
  };

  const filteredContacts = useMemo(() => {
    return contacts.filter(c => {
      if (filterOrigen !== "todos" && c.formulario_origen !== filterOrigen) return false;
      if (filterEstado !== "todos" && c.estado !== filterEstado) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const searchable = [c.nombre, c.contacto, c.email, c.clinica, c.telefono, c.tipo_clinica].filter(Boolean).join(" ").toLowerCase();
        if (!searchable.includes(term)) return false;
      }
      return true;
    });
  }, [contacts, filterOrigen, filterEstado, searchTerm]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#101421' }}>
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#101421' }}>
      <Toaster position="top-center" richColors theme="dark" />

      {/* Header */}
      <header className="border-b border-gray-800 bg-[#101421]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="IA Sanitaria" className="h-10 w-auto" />
            <div>
              <h1 className="text-white font-bold text-lg">Panel Admin</h1>
              <p className="text-gray-500 text-xs">{user?.email}</p>
            </div>
            {/* Tabs */}
            <div className="flex items-center gap-1 ml-4 bg-gray-800/50 rounded-lg p-0.5">
              <button onClick={() => setActiveTab("crm")} className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "crm" ? "bg-blue-500 text-white" : "text-gray-400 hover:text-white"}`} data-testid="tab-crm">
                <Users className="w-4 h-4" /> CRM
              </button>
              <button onClick={() => setActiveTab("blog")} className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "blog" ? "bg-blue-500 text-white" : "text-gray-400 hover:text-white"}`} data-testid="tab-blog">
                <PenTool className="w-4 h-4" /> Blog
              </button>
              <button onClick={() => setActiveTab("analytics")} className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "analytics" ? "bg-blue-500 text-white" : "text-gray-400 hover:text-white"}`} data-testid="tab-analytics">
                <TrendingUp className="w-4 h-4" /> Analytics
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {activeTab === "crm" && (
              <>
                <Button onClick={fetchData} variant="ghost" size="sm" className="text-gray-400 hover:text-white" disabled={refreshing} data-testid="refresh-btn">
                  <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                </Button>
                <Button onClick={handleExportCSV} variant="outline" size="sm" className="border-gray-700 text-gray-300 hover:text-white" data-testid="export-csv-btn">
                  <DownloadIcon className="w-4 h-4" />
                  CSV
                </Button>
              </>
            )}
            <Button onClick={handleLogout} variant="ghost" size="sm" className="text-gray-400 hover:text-red-400" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === "blog" ? (
          <AdminBlog />
        ) : activeTab === "analytics" ? (
          <AdminAnalytics />
        ) : (
          <>
            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-grid">
                <StatCard icon={Users} label="Total" value={stats.total_contactos} color="bg-blue-500/20" />
                <StatCard icon={Phone} label="Contactos" value={stats.contactos_rapidos} color="bg-green-500/20" />
                <StatCard icon={FileText} label="Diagnósticos" value={stats.diagnosticos_completos} color="bg-purple-500/20" />
                <StatCard icon={BarChart3} label="Leads" value={stats.leads_checklist} color="bg-yellow-500/20" />
              </div>
            )}

            {/* Status summary pills */}
            {stats && (
              <div className="flex flex-wrap gap-3 mb-6">
                {ESTADOS.map(e => {
                  const count = stats[`estado_${e.value}`] || 0;
                  return (
                    <button
                      key={e.value}
                      onClick={() => setFilterEstado(filterEstado === e.value ? "todos" : e.value)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors ${filterEstado === e.value ? "border-blue-500 bg-blue-500/10 text-blue-400" : "border-gray-700 text-gray-400 hover:border-gray-600"}`}
                      data-testid={`filter-estado-${e.value}`}
                    >
                      <span className={`w-2 h-2 rounded-full ${e.color}`}></span>
                      {e.label}: {count}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Buscar por nombre, email, clínica..."
                  className="pl-10 bg-[#1A1F2E] border-gray-700 text-white"
                  data-testid="search-input"
                />
              </div>
              <Select value={filterOrigen} onValueChange={setFilterOrigen}>
                <SelectTrigger className="w-48 bg-[#1A1F2E] border-gray-700 text-white" data-testid="filter-origen">
                  <Filter className="w-4 h-4 text-gray-500 mr-2" />
                  <SelectValue placeholder="Filtrar por tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los tipos</SelectItem>
                  <SelectItem value="contacto_rapido">Contacto rápido</SelectItem>
                  <SelectItem value="diagnostico_completo">Diagnóstico</SelectItem>
                  <SelectItem value="lead_magnet">Lead checklist</SelectItem>
                </SelectContent>
              </Select>
              {(filterOrigen !== "todos" || filterEstado !== "todos" || searchTerm) && (
                <Button
                  onClick={() => { setFilterOrigen("todos"); setFilterEstado("todos"); setSearchTerm(""); }}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-white"
                  data-testid="clear-filters"
                >
                  <X className="w-4 h-4" />
                  Limpiar filtros
                </Button>
              )}
            </div>

            {/* Results count */}
            <p className="text-gray-500 text-sm mb-4" data-testid="results-count">
              {filteredContacts.length} de {contacts.length} contactos
            </p>

            {/* Contact list */}
            <div className="space-y-2">
              {filteredContacts.length === 0 ? (
                <div className="text-center py-12 text-gray-500" data-testid="empty-state">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No hay contactos que coincidan con los filtros</p>
                </div>
              ) : (
                filteredContacts.map(contact => (
                  <ContactRow
                    key={contact.id}
                    contact={contact}
                    onStatusChange={handleStatusChange}
                    onNotesUpdate={handleNotesUpdate}
                  />
                ))
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
